import React, { useState, useEffect } from 'react';
import { X, Folder, FolderOpen, ChevronRight, ChevronDown, Move, Copy, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../utils/api';

/**
 * Modal for moving or copying files to different folders
 * @param {Object} file - The file to move/copy
 * @param {string} mode - 'move' or 'copy'
 * @param {Function} onClose - Close modal callback
 * @param {Function} onSuccess - Success callback
 * @param {string} phase - Current phase filter
 */
const FileMoveModal = ({ file, mode = 'move', onClose, onSuccess, phase }) => {
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [expandedFolders, setExpandedFolders] = useState(new Set());

    const isMove = mode === 'move';

    useEffect(() => {
        fetchFolderTree();
    }, [phase]);

    const fetchFolderTree = async () => {
        try {
            setLoading(true);

            const response = await api.get('/folders/tree');

            if (response.data.folders) {
                setFolders(response.data.folders);
            }
        } catch (error) {
            console.error('Error fetching folder tree:', error);
            toast.error('Failed to load folders');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);


            const endpoint = isMove
                ? `/folders/files/${file.id}/move`
                : `/folders/files/${file.id}/copy`;

            const response = await api.post(endpoint, {
                folder_id: selectedFolder?.id || null
            });

            if (response.data.file) {
                toast.success(`File ${isMove ? 'moved' : 'copied'} successfully`);
                onSuccess?.(response.data.file);
                onClose();
            }
        } catch (error) {
            console.error(`Error ${isMove ? 'moving' : 'copying'} file:`, error);
            toast.error(error.response?.data?.message || `Failed to ${mode} file`);
        } finally {
            setSubmitting(false);
        }
    };

    const toggleExpand = (folderId) => {
        const newExpanded = new Set(expandedFolders);
        if (newExpanded.has(folderId)) {
            newExpanded.delete(folderId);
        } else {
            newExpanded.add(folderId);
        }
        setExpandedFolders(newExpanded);
    };

    // Build tree structure from flat list
    const buildTree = (items, parentId = null) => {
        return items
            .filter(item => item.parent_id === parentId)
            .map(item => ({
                ...item,
                children: buildTree(items, item.id)
            }));
    };

    const tree = buildTree(folders);

    const renderFolder = (folder, depth = 0) => {
        const isExpanded = expandedFolders.has(folder.id);
        const isSelected = selectedFolder?.id === folder.id;
        const hasChildren = folder.children && folder.children.length > 0;
        const isCurrentFolder = file.folder_id === folder.id;

        return (
            <div key={folder.id}>
                <div
                    className={`
            flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all
            ${isSelected ? 'bg-primary/10 border border-primary/30' : 'hover:bg-gray-50'}
            ${isCurrentFolder ? 'opacity-50 cursor-not-allowed' : ''}
          `}
                    style={{ paddingLeft: `${12 + depth * 20}px` }}
                    onClick={() => !isCurrentFolder && setSelectedFolder(folder)}
                >
                    {hasChildren ? (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(folder.id);
                            }}
                            className="p-0.5 hover:bg-gray-200 rounded"
                        >
                            {isExpanded ? (
                                <ChevronDown size={16} className="text-gray-400" />
                            ) : (
                                <ChevronRight size={16} className="text-gray-400" />
                            )}
                        </button>
                    ) : (
                        <div className="w-5" />
                    )}

                    {isExpanded ? (
                        <FolderOpen size={18} style={{ color: folder.color || '#6366f1' }} />
                    ) : (
                        <Folder size={18} style={{ color: folder.color || '#6366f1' }} />
                    )}

                    <span className={`text-sm ${isSelected ? 'font-medium text-primary' : 'text-gray-700'}`}>
                        {folder.name}
                        {folder.phase !== phase && (
                            <span className="ml-2 text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                {folder.phase}
                            </span>
                        )}
                    </span>

                    {isCurrentFolder && (
                        <span className="text-xs text-gray-400 ml-auto">(current)</span>
                    )}
                </div>

                {hasChildren && isExpanded && (
                    <div className="ml-2">
                        {folder.children.map(child => renderFolder(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-primary/5 to-transparent flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-xl ${isMove ? 'bg-blue-100' : 'bg-green-100'}`}>
                                {isMove ? (
                                    <Move className="text-blue-600" size={24} />
                                ) : (
                                    <Copy className="text-green-600" size={24} />
                                )}
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    {isMove ? 'Move' : 'Copy'} File
                                </h2>
                                <p className="text-sm text-gray-500 truncate max-w-[280px]" title={file.file_name}>
                                    {file.file_name}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X size={20} className="text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Folder Tree */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 size={32} className="animate-spin text-primary" />
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {/* Root option */}
                            <div
                                className={`
                  flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all
                  ${selectedFolder === null ? 'bg-primary/10 border border-primary/30' : 'hover:bg-gray-50'}
                  ${file.folder_id === null ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                                onClick={() => file.folder_id !== null && setSelectedFolder(null)}
                            >
                                <div className="w-5" />
                                <Folder size={18} className="text-gray-400" />
                                <span className={`text-sm ${selectedFolder === null ? 'font-medium text-primary' : 'text-gray-700'}`}>
                                    Root (No Folder)
                                </span>
                                {file.folder_id === null && (
                                    <span className="text-xs text-gray-400 ml-auto">(current)</span>
                                )}
                            </div>

                            {/* Folder tree */}
                            {tree.map(folder => renderFolder(folder))}

                            {folders.length === 0 && (
                                <div className="text-center py-8 text-gray-500">
                                    <Folder size={48} className="mx-auto mb-3 text-gray-300" />
                                    <p>No folders yet</p>
                                    <p className="text-sm">Create folders to organize your files</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-white transition-colors font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting || (selectedFolder?.id === file.folder_id) || (selectedFolder === null && file.folder_id === null)}
                        className={`
              flex-1 px-4 py-3 rounded-xl font-medium flex items-center justify-center gap-2
              transition-colors disabled:opacity-50 disabled:cursor-not-allowed
              ${isMove
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-green-600 text-white hover:bg-green-700'
                            }
            `}
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                {isMove ? 'Moving...' : 'Copying...'}
                            </>
                        ) : (
                            <>
                                {isMove ? <Move size={18} /> : <Copy size={18} />}
                                {isMove ? 'Move Here' : 'Copy Here'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FileMoveModal;
